import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, unwrapObject } from "@/lib/api";
import { useWhatsAppUpdates } from "@/lib/useWhatsAppUpdates";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ExternalLink, Loader2 } from "lucide-react";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { WaAvatar } from "./WaParts";
import { ChatPane } from "./ChatPane";
import type { WaConversation } from "@/types/whatsapp";

interface WhatsAppChatDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  phone: string;
  name?: string;
  dealId?: string;
  contactId?: string;
  leadId?: string;
  /** After a message is sent (e.g. refresh the deal timeline) */
  onChanged?: () => void;
}

/** WhatsApp chat in a popup, opened from a deal / contact / lead */
export function WhatsAppChatDialog({ open, onOpenChange, phone, name, dealId, contactId, leadId, onChanged }: WhatsAppChatDialogProps) {
  const [conv, setConv] = useState<WaConversation | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [refresh, setRefresh] = useState(0);

  useEffect(() => {
    if (!open) return;
    setConv(null);
    setProblem(null);
    api
      .post("/api/WhatsApp/Start", { phone, deal_id: dealId, contact_id: contactId, lead_id: leadId })
      .then((res) => setConv(unwrapObject<WaConversation>(res)))
      .catch((err) => setProblem(err.message || "Could not open the chat"));
  }, [open, phone, dealId, contactId, leadId]);

  useWhatsAppUpdates((e) => {
    if (!e || e.conversation_id === conv?.id) setRefresh((n) => n + 1);
  }, open && Boolean(conv));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="wa sm:max-w-2xl p-0 gap-0 overflow-hidden h-[85vh] flex flex-col border-0">
        <DialogHeader className="h-[59px] px-4 shrink-0 flex-row items-center gap-3 space-y-0 text-left pr-12" style={{ background: "var(--wa-header)" }}>
          <WaAvatar size={40} />
          <div className="min-w-0 flex-1">
            <DialogTitle className="text-[16px] font-normal truncate" style={{ color: "var(--wa-text)" }}>
              {name || conv?.profile_name || phone}
            </DialogTitle>
            <DialogDescription className="flex items-center gap-3 text-[13px]" style={{ color: "var(--wa-muted)" }}>
              <span>{conv?.phone || phone}</span>
              {conv && (
                <Link to={`/whatsapp?c=${conv.id}`} className="inline-flex items-center gap-1 hover:underline" style={{ color: "var(--wa-green)" }}>
                  Open in inbox <ExternalLink className="size-3" />
                </Link>
              )}
            </DialogDescription>
          </div>
          <WhatsAppIcon className="size-6" />
        </DialogHeader>
        {problem ? (
          <div className="p-6 text-sm text-destructive">{problem}</div>
        ) : !conv ? (
          <div className="wa-wallpaper flex-1 flex items-center justify-center">
            <Loader2 className="size-6 animate-spin" style={{ color: "var(--wa-green)" }} />
          </div>
        ) : (
          <ChatPane conversationId={conv.id} refreshSignal={refresh} onChanged={onChanged} className="flex-1" />
        )}
      </DialogContent>
    </Dialog>
  );
}
